import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend as RechartsLegend,
} from "recharts";
import {
  Droplets,
  Truck,
  IndianRupee,
  AlertTriangle,
  MapPin,
  Activity,
  Filter,
  Users,
  Clock,
  Navigation,
} from "lucide-react";
import { useSelector } from "react-redux";
import { ImProfile } from "react-icons/im";
import { RiWaterFlashLine } from "react-icons/ri";

const chartData = {
  daily: [
    { name: "08:00", water: 40, revenue: 2400 },
    { name: "10:00", water: 80, revenue: 4800 },
    { name: "12:00", water: 120, revenue: 7200 },
    { name: "14:00", water: 100, revenue: 6000 },
    { name: "16:00", water: 60, revenue: 3600 },
    { name: "18:00", water: 90, revenue: 5400 },
  ],
  weekly: [
    { name: "Mon", water: 400, revenue: 24000 },
    { name: "Tue", water: 300, revenue: 18000 },
    { name: "Wed", water: 500, revenue: 30000 },
    { name: "Thu", water: 278, revenue: 16000 },
    { name: "Fri", water: 189, revenue: 11000 },
    { name: "Sat", water: 239, revenue: 14000 },
    { name: "Sun", water: 349, revenue: 20000 },
  ],
  monthly: [
    { name: "Week 1", water: 2400, revenue: 124000 },
    { name: "Week 2", water: 1398, revenue: 80000 },
    { name: "Week 3", water: 9800, revenue: 500000 },
    { name: "Week 4", water: 3908, revenue: 200000 },
  ],
};

const alertsData = [
  {
    id: 1,
    type: "critical",
    message: "Illegal tapping detected at Sector 4",
    time: "10 min ago",
  },
  {
    id: 2,
    type: "warning",
    message: "Tanker TN-04 Route Deviation",
    time: "25 min ago",
  },
  {
    id: 3,
    type: "info",
    message: "Waiting anomaly at Station A-12",
    time: "1 hr ago",
  },
];

const Card = ({ children, className = "" }) => (
  <div className={` rounded-xl border border-slate-200 shadow-sm ${className}`}>
    {children}
  </div>
);

const Badge = ({ children, type = "neutral" }) => {
  const styles = {
    neutral: "bg-slate-100 text-slate-600",
    success: "bg-emerald-100 text-emerald-700",
    danger: "bg-red-100 text-red-700",
    warning: "bg-amber-100 text-amber-700",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${styles[type]}`}
    >
      {children}
    </span>
  );
};

export default function WaterOperationsDashboard() {
  const themeMode = useSelector((state) => state.theme.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";
  const scrollBg = themeMode ? "sidebar_scroll_dark" : "sidebar_scroll";
  const cardBg = themeMode ? "table_hed_bg" : "bg-white";

  const [timeFilter, setTimeFilter] = useState("weekly");

  const getTotalWater = () => {
    if (timeFilter === "daily") return "490 KL";
    if (timeFilter === "weekly") return "2,255 KL";
    return "17,500 KL";
  };

  const getTotalRevenue = () => {
    if (timeFilter === "daily") return "₹ 29,400";
    if (timeFilter === "weekly") return "₹ 1,33,000";
    return "₹ 9,04,000";
  };

  return (
    <div className={`w-full ${bgColorClass} h-full`}>
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderThem} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center  gap-x-2`}
        >
          <p>
            <RiWaterFlashLine className="cursor-pointer font-semibold" />
          </p>
          <p className="font-semibold">Water Operations</p>
        </div>
         <div className={`flex ${bgColorClass}e border ${borderThem} rounded-lg p-1 shadow-sm`}>
              {["daily", "weekly", "monthly"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setTimeFilter(tab)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md capitalize transition-all ${
                    timeFilter === tab
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  {tab} Data
                </button>
              ))}
            </div>
      </div>

      <div className=" h-[calc(100vh-50px)] pb-10 overflow-y-auto ">
        <div className="px-3 md:px-5 lg:px-12 md:mt-5 mt-3">
          

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card
              className={`p-5 border-l-4 ${bgColorClass} ${borderThem} !border-l-blue-500`}
            >
              <div className="flex justify-between items-start">
                <div className={`${textColorClass}`}>
                  <p className="text-xs font-medium  uppercase">
                    Filling Stations
                  </p>
                  <h3 className="text-2xl font-bold  mt-1">50 Total</h3>
                </div>
                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                  <Droplets className="w-5 h-5" />
                </div>
              </div>
              <div className="flex gap-3 mt-4 text-sm">
                <span className="flex items-center text-emerald-600 font-medium">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mr-1.5"></div>
                  42 Active
                </span>
                <span className={`flex items-center ${textColorClass} font-medium`}>
                  <div className="w-2 h-2  rounded-full bg-slate-300 mr-1.5"></div>
                  8 Inactive
                </span>
              </div>
            </Card>

            <Card
              className={`p-5 border-l-4 ${bgColorClass} ${borderThem} !border-l-blue-500`}
            >
              <div className="flex justify-between items-start">
                <div className={`${textColorClass}`}>
                  <p className="text-xs font-medium uppercase">Fleet Status</p>
                  <h3 className="text-2xl font-bold  mt-1">150 Reg.</h3>
                </div>
                <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                  <Truck className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="bg-slate-50 rounded px-2 py-1">
                  <p className="text-xs text-slate-500">Active Today</p>
                  <p className="font-bold text-slate-700">124</p>
                </div>
                <div className="bg-indigo-50 rounded px-2 py-1">
                  <p className="text-xs text-indigo-600 font-bold">En-route</p>
                  <p className="font-bold text-indigo-700 animate-pulse">
                    45 Moving
                  </p>
                </div>
              </div>
            </Card>

            <Card
              className={`p-5 border-l-4 ${bgColorClass} ${borderThem} !border-l-emerald-500`}
            >
              <div className="flex justify-between items-start">
                <div className={`${textColorClass}`}>
                  <p className="text-xs font-medium uppercase">
                    Supply & Revenue
                  </p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <h3 className="text-2xl font-bold ">{getTotalRevenue()}</h3>
                  </div>
                  <p className="text-sm font-semibold text-blue-600 mt-1">
                    {getTotalWater()} Supplied
                  </p>
                </div>
                <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                  <IndianRupee className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 text-xs text-slate-400">
                Showing data for:{" "}
                <span className={`font-bold capitalize ${textColorClass}`}>
                  {timeFilter}
                </span>
              </div>
            </Card>

            <Card
              className={`p-5 border-l-4 ${bgColorClass} ${borderThem} !border-l-amber-500`}
            >
              <div className="flex justify-between items-start">
                <div className={`${textColorClass}`}>
                  <p className="text-xs font-medium uppercase">
                    Action Required
                  </p>
                  <h3 className="text-2xl font-bold  mt-1">5 Alerts</h3>
                </div>
                <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
                  <AlertTriangle className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                <div className={`flex items-center gap-2 text-sm ${textColorClass}`}>
                  <Users className="w-4 h-4 text-blue-500" />
                  <span className="font-bold">42</span> Live Orders
                </div>
                <Badge type="danger">3 Critical</Badge>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card
              className={`lg:col-span-2 ${borderThem} flex flex-col min-h-[500px] overflow-hidden`}
            >
              <div
                className={`p-4 border-b ${borderThem}  ${bgColorClass} flex justify-between items-center`}
              >
                <h3
                  className={`font-bold ${textColorClass} flex items-center gap-2`}
                >
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Live GIS Map
                </h3>
                <div className="flex gap-2 text-xs">
                  <span className="flex items-center px-2 py-1 bg-green-50 text-green-700 rounded border border-green-200">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1" />{" "}
                    Station
                  </span>
                  <span className="flex items-center px-2 py-1 bg-red-50 text-red-700 rounded border border-red-200">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-1" />{" "}
                    Illegal
                  </span>
                  <span className="flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200">
                    <Navigation className="w-3 h-3 mr-1" /> Tanker
                  </span>
                </div>
              </div>

              <div className="relative flex-1 bg-slate-200 w-full group">
                <div
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage:
                      "linear-gradient(#94a3b8 1px, transparent 1px), linear-gradient(90deg, #94a3b8 1px, transparent 1px)",
                    backgroundSize: "40px 40px",
                  }}
                ></div>

                <div className="absolute top-1/3 left-1/3 flex flex-col items-center">
                  <div className="w-6 h-6 bg-green-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <span className="text-[8px] text-white font-bold">S1</span>
                  </div>
                  <span className="mt-1 text-xs font-bold bg-white/80 px-1 rounded shadow-sm backdrop-blur-sm">
                    Station 1
                  </span>
                </div>

                <div className="absolute top-1/4 right-1/4 animate-pulse cursor-pointer">
                  <div className="w-8 h-8 bg-red-500/30 rounded-full flex items-center justify-center">
                    <div className="w-4 h-4 bg-red-600 rounded-full shadow-lg border-2 border-white"></div>
                  </div>
                  <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap">
                    Illegal Tap!
                  </span>
                </div>

                <div className="absolute bottom-1/3 right-1/3 transition-all duration-1000 ease-linear hover:scale-110 cursor-pointer">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-blue-400 rounded-full opacity-30 animate-ping"></div>
                    <div className="w-8 h-8 bg-blue-600 rounded-full border-2 border-white shadow-xl flex items-center justify-center text-white z-10 relative">
                      <Truck className="w-4 h-4" />
                    </div>
                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[9px] px-1.5 py-0.5 rounded font-mono">
                      TN-04-123
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex flex-col gap-6">
              <Card className={`flex-1 ${borderThem} overflow-hidden flex flex-col`}>
                <div className={`p-4 border-b ${borderThem} ${bgColorClass}`}>
                  <h3 className={`font-bold ${textColorClass} flex items-center gap-2`}>
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Live Alerts (3)
                  </h3>
                </div>
                <div className={`p-2 overflow-y-auto flex-1 ${bgColorClass}`}>
                  {alertsData.map((alert) => (
                    <div
                      key={alert.id}
                      className={`mb-2 p-3 ${bgColorClass} border ${borderThem} rounded-lg shadow-sm hover:shadow-md transition-shadow`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <Badge
                          type={
                            alert.type === "critical"
                              ? "danger"
                              : alert.type === "warning"
                              ? "warning"
                              : "neutral"
                          }
                        >
                          {alert.type}
                        </Badge>
                        <span className={`text-[10px] ${textColorClass} flex items-center`}>
                          <Clock className="w-3 h-3 mr-1" /> {alert.time}
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${textColorClass} leading-snug`}>
                        {alert.message}
                      </p>
                    </div>
                  ))}
                  <button className="w-full py-2 mt-2 text-xs font-semibold text-blue-600 bg-blue-50 rounded border border-blue-100 hover:bg-blue-100">
                    View All Incidents
                  </button>
                </div>
              </Card>

              <Card className={`h-[250px] p-4 ${borderThem} flex flex-col`}>
                <div className="mb-2">
                  <h3 className={`text-sm font-bold ${textColorClass}`}>
                    Revenue Trend
                  </h3>
                  <p className="text-xs text-slate-500">
                    {timeFilter} analysis
                  </p>
                </div>
                <div className="flex-1 w-full min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData[timeFilter]}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e2e8f0"
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "none",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        }}
                        labelStyle={{ fontSize: "12px", color: "#64748b" }}
                      />
                      <Bar
                        dataKey="revenue"
                        fill="#10b981"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </div>
          </div>

          <Card className={`mt-6 p-6 ${borderThem}`}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`text-lg font-bold ${textColorClass}`}>
                Water Usage Analytics ({timeFilter})
              </h3>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${textColorClass}`}>
                  Units: KL (Kiloliters)
                </span>
              </div>
            </div>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData[timeFilter]}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <Tooltip
                    cursor={{ fill: "#f1f5f9" }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                    }}
                  />
                  <RechartsLegend />
                  <Bar
                    dataKey="water"
                    name="Water Supplied (KL)"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={60}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
